/**
 * Composable для безопасной работы с гидратацией
 * Помогает избежать ошибок несоответствия SSR и клиентского рендеринга
 */

export const useHydrationSafe = () => {
  const isHydrated = ref(false)
  
  onMounted(() => {
    isHydrated.value = true
  })
  
  /**
   * Безопасное получение значения, которое может отличаться между сервером и клиентом
   * @param clientValue - значение для клиента
   * @param serverValue - значение для сервера (по умолчанию null)
   */
  const getSafeValue = <T>(clientValue: T, serverValue: T | null = null): T | null => {
    return isHydrated.value ? clientValue : serverValue
  }
  
  /**
   * Проверка, что мы находимся на клиенте и компонент гидратирован
   */
  const isClient = computed(() => isHydrated.value && import.meta.client)
  
  /**
   * Безопасное добавление пассивных event listeners после гидратации
   * @param target - элемент или объект для добавления listener
   * @param event - название события
   * @param handler - обработчик события
   * @param options - опции для addEventListener (по умолчанию passive: true)
   */
  const addPassiveListener = (
    target: EventTarget,
    event: string,
    handler: EventListener,
    options: AddEventListenerOptions = { passive: true }
  ) => {
    if (isClient.value) {
      target.addEventListener(event, handler, options)
      
      // Возвращаем функцию для удаления listener
      return () => {
        target.removeEventListener(event, handler, options)
      }
    }
    return () => {} // Пустая функция если не на клиенте
  }

  return {
    isHydrated: readonly(isHydrated),
    isClient: readonly(isClient),
    getSafeValue,
    addPassiveListener
  }
}
